/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useEffect, useRef } from 'react';
import {
  CalenderContiner,
  CpdsDatePickerButton,
  CpdsDatePickerItem,
  CpdsDatePickerItemImg,
  CpdsDatePickerItemLeading,
  CpdsDatePickerItemLink,
  CpdsDatePickerItems,
  CpdsDatePickerItemTrailing,
  CpdsDatePickerStyleTag,
  DatePickerPopupOptionList,
  DatePickerPopupActionHeader,
  DatePickerPopupCloseButton,
  DatePickerPopupOptions,
  DatePickerPopupSeperator,
  DatePickerPopupButtonContainer,
} from './styles/cpds-date-picker-style';
import { CpdsDatepickerProps } from './typings';
import { themeStyleClass } from '../utils/cpds-utils';
import { checkValue, localization } from 'src/components/common/helper';
import { Cpds } from '@Cpds.Ui';
import { css } from 'styled-components';
import { UitkCalendarNavigation } from 'uitk-react-calendar';
import { useStores } from 'src/utils/useStore';
import { useWindowDimensions } from 'src/components/common/useWindowDimentions';

const CpdsDatepicker: React.FC<CpdsDatepickerProps> = (props) => {
  const {
    domRef,
    exClasses = '',
    styleOverrides,
    children,
    label,
    maxItemsToShow,
    visibleItems,
    className: _className,
    ...rest
  } = props;
  const { context } = useStores();
  const { width: windowWidth } = useWindowDimensions();

  const today = new Date();

  let options = [
    { ItemText: 'Previous Week', ItemValue: 'previous-week' },
    { ItemText: 'This Week', ItemValue: 'this-week' },
    { ItemText: 'Next Week', ItemValue: 'next-week' },
    { ItemText: 'Pick a Date', ItemValue: 'pick-a-date' },
  ];

  const initValue = options.filter((option) => option.ItemText === label)?.[0]?.ItemValue;

  const { themeColorMode, themeTypography } = rest;
  const [isCalendar, setCalendarView] = React.useState(false);
  const [hideDropdown, setHideDropdown] = React.useState(true);
  const [selectedValue, setSelectedvalue] = React.useState(initValue ?? '');
  const dropDownOptionViewRef = useRef(null);
  const [state, setState] = React.useState({
    year: today.getFullYear(),
    month: today.getMonth(), // zero-based
    selectionStart: null,
    selectionEnd: null,
  });

  const className = themeStyleClass('cpds-drop-down-wrapper', themeTypography, exClasses);

  const handleSelectionChanged = (selectionStart, selectionEnd) => {
    setState({
      ...state,
      selectionStart,
      selectionEnd,
    });
  };

  options = checkValue(visibleItems, true)
    ? options.filter((option) => visibleItems.includes(option.ItemText))
    : options;

  const onClickDatePickerItem = (item, event) => {
    // event.preventDefault();
    if (item.ItemValue === 'pick-a-date') {
      setCalendarView(true);
    } else {
      setState({
        year: today.getFullYear(),
        month: today.getMonth(), // zero-based
        selectionStart: null,
        selectionEnd: null,
      });
      setSelectedvalue(item.ItemValue);
      setCalendarView(false);
      setHideDropdown(true);
      props.onItemChange(item.ItemText);
    }
  };

  const handleConfirm = () => {    
    const { selectionStart, selectionEnd } = state;
    if (selectionStart && selectionEnd) {
      
      setSelectedvalue('pick-a-date');
      const startDate = new Date(selectionStart);
      const endDate = new Date(selectionEnd);

      const options = {
        skeleton: 'MMMd',
        locale: context?.locale || 'en',
      };
      const _startDate = localization.formatDate(new Date(startDate), options);

      const _endDate = localization.formatDate(new Date(endDate), options);

      props.onItemChange(`${_startDate} - ${_endDate}`);
      setCalendarView(false);
      setHideDropdown(true);
    }
  };

  const renderLeadingComponent = (option: any, themeColorMode: string, index) => {
    return (
      <Cpds.Icon
        id={`L-key-${index}`}
        name={option.ItemLeading.name || 'check'}
        size={option.ItemLeading.size || 'small'}
      />
    );
  };

  const renderItems = (option: any, themeColorMode: string, index) => {
    return (
      <CpdsDatePickerItem
        selected={selectedValue === option.ItemValue}
        key={index}
        className="cpds-drop-down-item"
        themeColorMode={themeColorMode}
        onClick={(e) => onClickDatePickerItem(option, e)}
      >
        {checkValue(option.ItemLeading) && (
          <CpdsDatePickerItemLeading className="cpds-drop-down-item-leading">
            {renderLeadingComponent(option, themeColorMode, index)}
          </CpdsDatePickerItemLeading>
        )}
        <CpdsDatePickerItemLink className="cpds-drop-down-item-link" onClick={(e) => onClickDatePickerItem(option, e)}>
          {option.ItemText}
        </CpdsDatePickerItemLink>
        {checkValue(option.ItemValue === 'pick-a-date') && (
          <CpdsDatePickerItemTrailing className="cpds-drop-down-item-trailing">
            <Cpds.Icon id={`key-${index}`} name={'chevron-right'} size={'extra_small'} />
          </CpdsDatePickerItemTrailing>
        )}
      </CpdsDatePickerItem>
    );
  };

  const handleDropDownClick = () => {
    setHideDropdown(!hideDropdown);
  };

  //close the calendar view when dropdown is closed
  useEffect(() => {
    if (hideDropdown) {
      setCalendarView(false);
    }
  }, [hideDropdown]);

  //hide the dropdown when clicked outside
  useEffect(() => {
    // Click event handler
    function handleClickOutside(event) {
      if (dropDownOptionViewRef.current && !dropDownOptionViewRef.current.contains(event.target)) {
        setHideDropdown(true);
      }
    }
    // Add the click event listener
    document.addEventListener('mousedown', handleClickOutside);

    // Cleanup the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <CpdsDatePickerStyleTag
      className={className}
      ref={dropDownOptionViewRef}
      styleOverrides={styleOverrides}
      themeColorMode={themeColorMode}
      isOpen={!hideDropdown}
    >
      <CpdsDatePickerButton
        className="cpds-drop-down-button cpds-meta-roman-medium-book-09"
        {...rest}
        disabled={rest.state === 'DISABLED' ? true : false}
        themeColorMode={themeColorMode}
        onClick={() => handleDropDownClick()}
        isOpen={!hideDropdown}
      >
        {label || 'Label'}
        <Cpds.Icon
          styleOverrides={css`
            margin-left: 4px;
          `}
          id="dorpdown-icon"
          name="chevron-down"
          customSize={{ width: '10', height: '10' }}
        />
      </CpdsDatePickerButton>
      {/* <Cpds.Button 
        exClasses='cpds-drop-down-button'
        {...rest} 
        disabled={rest.state === 'DISABLED' ? true : false}
        themeColorMode={themeColorMode} 
        tag='button' buttonType='Tertiary'>
        {label || 'Label'}
        <Cpds.Icon id='dorpdown-icon' name='chevron-down' />
      </Cpds.Button> */}
      {rest.state !== 'DISABLED' && (
        <Cpds.Viewport>
          <Cpds.ViewportMedium>
            <CpdsDatePickerItems
              maxItemsToShow={maxItemsToShow}
              className="cpds-drop-down-items cpds-meta-roman-medium-book-09"
              themeColorMode={themeColorMode}
              isCalendar={isCalendar}
            >
              {isCalendar ? (
                <CalenderContiner key={`calendar-view`} className="cpds-drop-down-item">
                  <div style={{ display: 'flex', flexDirection: 'row', justifyContent: 'space-between' }}>
                    <Cpds.Button
                      exClasses={_className}
                      tag="a"
                      buttonSize="small"
                      onClick={() => setCalendarView(false)}
                    >
                      <Cpds.Icon id="calender-back" name="chevron-left" customSize={{ width: '10', height: '10' }} />{' '}
                      Back
                    </Cpds.Button>
                    <Cpds.Button exClasses={_className} tag="a" buttonSize="small" onClick={() => handleConfirm()}>
                      Confirm
                      <Cpds.Icon id="calender-back" name="chevron-right" customSize={{ width: '10', height: '10' }} />
                    </Cpds.Button>
                  </div>
                  <div style={{ margin: '24px 0 12px 0', width: '100%' }}>
                    <hr style={{ height: '0px', borderBlockStart: '1px solid #D1D5E2' }} />
                  </div>
                  <UitkCalendarNavigation
                    locale={'en-US'}
                    month={state.month}
                    year={state.year}
                    numMonthsToDisplay={1}
                    numColumns={1}
                    selectable={true}
                    showSiblingMonths={true}
                    stretchy={false}
                    selectionStart={state.selectionStart}
                    selectionEnd={state.selectionEnd}
                    onSelectionChanged={handleSelectionChanged}
                    minDayCellHeight={48}
                  />
                </CalenderContiner>
              ) : maxItemsToShow > 0 && checkValue(maxItemsToShow) ? (
                <Cpds.scrollBar>
                  {options.map((option, index) => renderItems(option, themeColorMode, index))}
                </Cpds.scrollBar>
              ) : (
                options.map((option, index) => renderItems(option, themeColorMode, index))
              )}
            </CpdsDatePickerItems>
          </Cpds.ViewportMedium>
          <Cpds.ViewportSmall>
            <DatePickerPopupOptionList>
              <Cpds.scrollBar>
                <DatePickerPopupActionHeader>
                  <DatePickerPopupCloseButton onClick={()=> handleDropDownClick()}>
                    <Cpds.Icon id="close-icon" name="close" />
                  </DatePickerPopupCloseButton>
                </DatePickerPopupActionHeader>
                <DatePickerPopupOptions>
                  {options
                    .filter((option) => option?.ItemValue !== 'pick-a-date')
                    .map((option) => ({
                      ...option,
                      ...(selectedValue === option.ItemValue ? { ItemLeading: { name: 'check', size: 'small' } } : {}),
                    }))
                    .map((option, index) => renderItems(option, themeColorMode, index))}
                </DatePickerPopupOptions>
                <DatePickerPopupSeperator />
                <UitkCalendarNavigation
                  locale={'en-US'}
                  month={state.month}
                  year={state.year}
                  numMonthsToDisplay={1}
                  numColumns={1}
                  selectable={true}
                  showSiblingMonths={true}
                  stretchy={false}
                  selectionStart={state.selectionStart}
                  selectionEnd={state.selectionEnd}
                  onSelectionChanged={handleSelectionChanged}
                  minDayCellHeight={48}
                />
                <DatePickerPopupButtonContainer>
                  <DatePickerPopupSeperator />
                  <Cpds.Button
                    buttonType="Primary"
                    tag="button"
                    buttonSize="large"
                    onClick={() => handleConfirm()}
                  >
                    Confirm
                  </Cpds.Button>
                </DatePickerPopupButtonContainer>
              </Cpds.scrollBar>
            </DatePickerPopupOptionList>
          </Cpds.ViewportSmall>
        </Cpds.Viewport>
      )}
    </CpdsDatePickerStyleTag>
  );
};

export default CpdsDatepicker;
